import { buildPermissionGroups } from "./permission-groups";

export type PermissionLevel = "VIEW" | "MANAGE" | "SPECIFIC";
export type PermissionRisk = "NORMAL" | "HIGH";
export type ResourceAccessLevel = "NONE" | "VIEW" | "MANAGE";

export interface PermissionPresentation {
  code: string;
  section: string;
  titleKey: string;
  descriptionKey: string;
  level: PermissionLevel;
  risk: PermissionRisk;
}

const sharedSectionByRole = new Map(
  buildPermissionGroups().flatMap((group) =>
    group.permissions.map((code) => [code, group.group] as const),
  ),
);

const permission = (
  code: string,
  section?: string,
  level: PermissionLevel = "SPECIFIC",
  risk: PermissionRisk = "NORMAL",
): PermissionPresentation => ({
  code,
  section: section || sharedSectionByRole.get(code) || "ADMINISTRATION",
  titleKey: `ACCESS.PERMISSIONS.${code}.TITLE`,
  descriptionKey: `ACCESS.PERMISSIONS.${code}.DESCRIPTION`,
  level,
  risk,
});

export const BUSINESS_PERMISSION_PRESENTATION: PermissionPresentation[] = [
  permission("CONTACT_VIEW", "CONTACTS", "VIEW"),
  permission("MANAGE_CONTACT", "CONTACTS", "MANAGE"),
  permission("ORDERS_VIEW", "ORDERS", "VIEW"),
  permission("ORDERS", "ORDERS", "MANAGE"),
  permission("TICKET_VIEW", "TICKETS", "VIEW"),
  permission("MANAGE_TICKET", "TICKETS", "MANAGE"),
  permission("PRODUCT_VIEW", "PRODUCTS", "VIEW"),
  permission("PAYMENT_VIEW", "PAYMENTS", "VIEW"),
  permission("CONVERSATION_VIEW", "CONVERSATIONS", "VIEW"),
  permission("BROADCAST_VIEW", "BROADCASTS", "VIEW"),
  permission("VOUCHER_VIEW", "VOUCHER", "VIEW"),
  permission("WORKFLOW_VIEW", "WORKFLOWS", "VIEW"),
  permission("MANAGE_AGENT", "ADMINISTRATION"),
  permission("MANAGE_AGENT_GROUP", "ADMINISTRATION"),
  permission("MANAGE_TICKET_ASSIGNMENT", "TICKETS"),
  permission("COMPANY_ALL_NUMBERS", "ADMINISTRATION", "SPECIFIC", "HIGH"),
  permission("COMPANY_MANAGE", "ADMINISTRATION"),
  permission("AUDIT_MANAGE", "ADMINISTRATION"),
  permission("REFERENTIAL_MANAGE", "ADMINISTRATION"),
  permission("MANAGE_ATTRIBUTE", "ADMINISTRATION"),
  permission("TEAM_INBOX", "CONVERSATIONS"),
  permission("BROADCAST", "BROADCASTS"),
  permission("BROADCAST_ADD", "BROADCASTS"),
  permission("BROADCAST_HISTORY", "BROADCASTS"),
  permission("BROADCAST_SCHEDULE", "BROADCASTS"),
  permission("AUTOMATION", "WORKFLOWS", "MANAGE"),
  permission("AUTOMATION_DEF_ACTIONS", "WORKFLOWS"),
  permission("MANAGE_FLOW", "WORKFLOWS"),
  permission("MANAGE_BOTS", "WORKFLOWS"),
  permission("MANAGE_AIASSISTANT", "WORKFLOWS"),
  permission("CONSULT_NOTIFS", "MESSAGING"),
  permission("PRODUCT", "PRODUCTS"),
  permission("PARCELS", "DELIVERY"),
  permission("MERCHANT", "DELIVERY"),
  permission("MANAGE_DELIVERYMAN", "DELIVERY"),
  permission("ANALYTICS_CHAT", "ANALYTICS"),
  permission("MANAGE_VOUCHER", "VOUCHER"),
  permission("MANAGE_PAYMENT_LINK", "PAYMENTS"),
  permission("MANAGE_SAVED_CARDS", "PAYMENTS"),
  permission("MANAGE_PAYMENT_AUTHZ", "PAYMENTS"),
  permission("PAYMENT_SIM_ACCESS", "PAYMENTS"),
  permission("MANAGE_TEMPLATE", "WORKFLOWS"),
  permission("REFERENTIAL_VAR_MANAGE", "MESSAGING"),
  permission("REFERENTIAL_DEVISE_MANAGE", "ADMINISTRATION"),
  permission("CATALOG_SUBSCRIPTION", "ADMINISTRATION"),
  permission("ACTION_ON_TEMPLATE", "WORKFLOWS"),
  permission("MANAGE_PAYMENT_SCHEDULES", "PAYMENTS"),
  permission("RESERVATION_HOSTELS", "ORDERS"),
  permission("MANAGE_SUBSCRIPTION", "ADMINISTRATION"),
  permission("PAYMENT_ONBOARDING_VIEW", "PAYMENT_ONBOARDING", "VIEW"),
  permission("MANAGE_PAYMENT_ONBOARDING", "PAYMENT_ONBOARDING", "MANAGE", "HIGH"),
];

export const COMPANY_HIDDEN_ROLE_CODES = new Set([
  "MANAGE_PAYMENT_CONFIG",
  "PARCELS",
  "MERCHANT",
  "MANAGE_DELIVERYMAN",
]);

export const COMPANY_ASSIGNABLE_RESOURCE_SECTIONS = new Set([
  "CONTACTS",
  "CONVERSATIONS",
  "TICKETS",
  "ORDERS",
  "PRODUCTS",
  "PAYMENTS",
  "PAYMENT_ONBOARDING",
  "BROADCASTS",
  "MESSAGING",
  "WORKFLOWS",
  "VOUCHER",
  "ADMINISTRATION",
  "ANALYTICS",
]);

export interface ResourcePermissionDefinition {
  section: string;
  view?: string;
  manage: readonly string[];
}

export const RESOURCE_PERMISSION_LEVELS: readonly ResourcePermissionDefinition[] = [
  { section: "CONTACTS", view: "CONTACT_VIEW", manage: ["MANAGE_CONTACT"] },
  { section: "CONVERSATIONS", view: "CONVERSATION_VIEW", manage: ["TEAM_INBOX"] },
  {
    section: "TICKETS",
    view: "TICKET_VIEW",
    manage: ["MANAGE_TICKET", "MANAGE_TICKET_ASSIGNMENT"],
  },
  { section: "ORDERS", view: "ORDERS_VIEW", manage: ["ORDERS", "RESERVATION_HOSTELS"] },
  { section: "PRODUCTS", view: "PRODUCT_VIEW", manage: ["PRODUCT"] },
  {
    section: "PAYMENTS",
    view: "PAYMENT_VIEW",
    manage: [
      "MANAGE_PAYMENT_LINK",
      "MANAGE_PAYMENT_AUTHZ",
      "MANAGE_SAVED_CARDS",
      "MANAGE_PAYMENT_SCHEDULES",
      "PAYMENT_SIM_ACCESS",
    ],
  },
  {
    section: "PAYMENT_ONBOARDING",
    view: "PAYMENT_ONBOARDING_VIEW",
    manage: ["MANAGE_PAYMENT_ONBOARDING"],
  },
  {
    section: "BROADCASTS",
    view: "BROADCAST_VIEW",
    manage: ["BROADCAST", "BROADCAST_ADD", "BROADCAST_HISTORY", "BROADCAST_SCHEDULE"],
  },
  {
    section: "WORKFLOWS",
    view: "WORKFLOW_VIEW",
    manage: [
      "AUTOMATION",
      "AUTOMATION_DEF_ACTIONS",
      "MANAGE_FLOW",
      "MANAGE_BOTS",
      "MANAGE_AIASSISTANT",
      "MANAGE_TEMPLATE",
      "ACTION_ON_TEMPLATE",
    ],
  },
  { section: "MESSAGING", manage: ["CONSULT_NOTIFS", "REFERENTIAL_VAR_MANAGE"] },
  { section: "VOUCHER", view: "VOUCHER_VIEW", manage: ["MANAGE_VOUCHER"] },
  {
    section: "ADMINISTRATION",
    manage: [
      "MANAGE_AGENT",
      "MANAGE_AGENT_GROUP",
      "COMPANY_MANAGE",
      "AUDIT_MANAGE",
      "REFERENTIAL_MANAGE",
      "REFERENTIAL_DEVISE_MANAGE",
      "MANAGE_ATTRIBUTE",
      "CATALOG_SUBSCRIPTION",
      "MANAGE_SUBSCRIPTION",
      "COMPANY_ALL_NUMBERS",
    ],
  },
  { section: "ANALYTICS", manage: ["ANALYTICS_CHAT"] },
] as const;

export const RESOURCE_PERMISSION_ROLE_CODES = new Set(
  RESOURCE_PERMISSION_LEVELS.flatMap((resource) => [
    ...(resource.view ? [resource.view] : []),
    ...resource.manage,
  ]),
);

/**
 * Sections excluded from NARROW_RESOURCE_ROLE_CODES because their roles are
 * ambiguous/broadly-granted (e.g. COMPANY_MANAGE, ANALYTICS_CHAT) rather than
 * naming one specific resource — the sidebar's group gate still needs to
 * discriminate those by opted-in company service, not by role alone.
 */
const AMBIGUOUS_RESOURCE_SECTIONS = new Set(["ADMINISTRATION", "ANALYTICS", "MESSAGING"]);

/**
 * Roles that unambiguously name one specific resource (Contacts, Orders,
 * Products, ...). An agent granted one of these directly (not via a
 * Keycloak group) should see that resource's menu item even with zero
 * group membership — see sidebar.component.ts's hasDirectResourceAccess.
 */
export const NARROW_RESOURCE_ROLE_CODES = new Set(
  RESOURCE_PERMISSION_LEVELS.filter((resource) => !AMBIGUOUS_RESOURCE_SECTIONS.has(resource.section))
    .flatMap((resource) => [...(resource.view ? [resource.view] : []), ...resource.manage]),
);

export function resourceLevelOptions(
  resource: ResourcePermissionDefinition,
): ResourceAccessLevel[] {
  return resource.view ? ["NONE", "VIEW", "MANAGE"] : ["NONE", "MANAGE"];
}
